import {
    EmailDef,
    PhoneNumberDef,
    SymbolConstantDef,
    SymbolSpecificDef,
    SymbolVariableDef
} from "@components/smart/GeneralFieldDefinition";
import BindingContext, { createPath } from "@odata/BindingContext";
import {
    CountryEntity,
    GovernmentInstitutionEntity,
    IGovernmentInstitutionEntity,
    PrGovernmentInstitutionContactEntity,
    PrGovernmentInstitutionDetailEntity,
    PrPayrollSettingEntity
} from "@odata/GeneratedEntityTypes";
import { GovernmentInstitutionTypeCode } from "@odata/GeneratedEnums";
import { isDefined } from "@utils/general";
import { capitalize } from "@utils/string";
import i18next from "i18next";

import { DASH_CHARACTER } from "../../../constants";
import { BasicInputSizes, CacheStrategy, FieldType, GroupedField } from "../../../enums";
import { TValue } from "../../../global.types";
import { TFieldDefinition, TFieldsDefinition } from "../../PageUtils";

export const GOVERNMENT_INSTITUTIONS = [
    PrPayrollSettingEntity.FinancialAdministration,
    PrPayrollSettingEntity.CzechSocialSecurityAdministration,
    PrPayrollSettingEntity.LaborOffice,
    PrPayrollSettingEntity.StatutoryInsuranceCompany
];

export function formatGovernmentInstitutionName(governmentInstitution: IGovernmentInstitutionEntity): string {
    return governmentInstitution?.ShortName?.replace("ÚP ", "") ?? "";
}

export function formatGovernmentInstitutionUfoName(name: string): string {
    let formattedStr = name?.replace("Finanční úřad pro ", "");
    formattedStr = formattedStr?.replace("Prahu", "Praha").trim();

    return capitalize(formattedStr);
}

export const governmentInstitutionLocalPath = BindingContext.localContext("GovernmentInstitution");

export function getFinancialAdministrationDef(): TFieldDefinition {
    return {
        type: FieldType.ComboBox,
        cacheStrategy: CacheStrategy.EndOfTime,
        filter: {
            select: `${GovernmentInstitutionEntity.GovernmentInstitutionTypeCode} eq '${GovernmentInstitutionTypeCode.FinancialAdministration}'`
        },
        formatter: (_: TValue, args) => formatGovernmentInstitutionName(args.item),
        fieldSettings: {
            displayName: "ShortName",
            shouldDisplayAdditionalColumns: true,
            preloadItems: true
        },
        columns: [{
            id: "ShortName",
            formatter: (_: TValue, args) => formatGovernmentInstitutionName(args.item)
        }, {
            id: "UfoName",
            formatter: (val: TValue) => formatGovernmentInstitutionUfoName(val as string)
        }]
    };
}

export const getContactsRowsDef = (): TFieldsDefinition => {
    const def = {} as TFieldsDefinition;
    for (const institution of GOVERNMENT_INSTITUTIONS) {
        let institutionLabel: string;
        switch (institution) {
            case PrPayrollSettingEntity.CzechSocialSecurityAdministration:
            case PrPayrollSettingEntity.FinancialAdministration:
                institutionLabel = i18next.t("GovernmentInstitution:Institution");
                break;
            case PrPayrollSettingEntity.LaborOffice:
                institutionLabel = i18next.t("GovernmentInstitution:LaborOffice");
                break;
            case PrPayrollSettingEntity.StatutoryInsuranceCompany:
                institutionLabel = i18next.t("GovernmentInstitution:StatutoryInsuranceCompany");
                break;
        }

        def[createPath(institution, PrGovernmentInstitutionDetailEntity.Contact, PrGovernmentInstitutionContactEntity.FirstName)] = {
            groupedField: GroupedField.MultiStart
        };
        def[createPath(institution, PrGovernmentInstitutionDetailEntity.Contact, PrGovernmentInstitutionContactEntity.LastName)] = {
            groupedField: GroupedField.MultiEnd
        };
        def[createPath(institution, PrGovernmentInstitutionDetailEntity.Contact, PrGovernmentInstitutionContactEntity.Email)] = {
            ...EmailDef,
            width: BasicInputSizes.XL,
        };
        def[createPath(institution, PrGovernmentInstitutionDetailEntity.Contact, PrGovernmentInstitutionContactEntity.PhoneNumber)] = {
            ...PhoneNumberDef
        };
        def[createPath(institution, governmentInstitutionLocalPath, GovernmentInstitutionEntity.Name)] = {
            isReadOnly: true,
            label: institutionLabel,
        };
        def[createPath(institution, governmentInstitutionLocalPath, GovernmentInstitutionEntity.Street)] = {
            label: i18next.t("GovernmentInstitution:Street"),
            isReadOnly: true,
        };
        def[createPath(institution, governmentInstitutionLocalPath, GovernmentInstitutionEntity.City)] = {
            label: i18next.t("GovernmentInstitution:City"),
            isReadOnly: true
        };
        def[createPath(institution, governmentInstitutionLocalPath, GovernmentInstitutionEntity.PostalCode)] = {
            label: i18next.t("GovernmentInstitution:PostalCode"),
            isReadOnly: true
        };
        def[createPath(institution, governmentInstitutionLocalPath, GovernmentInstitutionEntity.Country)] = {
            isReadOnly: true,
            label: i18next.t("GovernmentInstitution:Country"),
            formatter: (_, args) => {
                const countryEntityBc = args.storage.data.bindingContext.navigate(createPath(institution, governmentInstitutionLocalPath, GovernmentInstitutionEntity.Country));
                const name = args.storage.getValue(countryEntityBc.navigate(CountryEntity.Name));
                const code = args.storage.getValue(countryEntityBc.navigate(CountryEntity.Code));
                return isDefined(name) && isDefined(code) ? [name, code].join(" | ") : DASH_CHARACTER;
            }
        };
        def[createPath(institution, governmentInstitutionLocalPath, GovernmentInstitutionEntity.DataBoxId)] = {
            isReadOnly: true,
            label: i18next.t("GovernmentInstitution:DataBox"),
        };
        // def[createPath(institution, PrGovernmentInstitutionDetailEntity.AccountNumber)] = {};
        def[createPath(institution, PrGovernmentInstitutionDetailEntity.SymbolVariable)] = {
            ...SymbolVariableDef,
            groupedField: GroupedField.MultiStart
        };
        def[createPath(institution, PrGovernmentInstitutionDetailEntity.SymbolSpecific)] = {
            ...SymbolSpecificDef,
        };
        def[createPath(institution, PrGovernmentInstitutionDetailEntity.SymbolConstant)] = {
            ...SymbolConstantDef
        };
        def[createPath(institution, PrGovernmentInstitutionDetailEntity.RemittanceInformation)] = {
            groupedField: GroupedField.MultiEnd
        };
    }

    return def;
};
